import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  loginAndResolvePasswordState,
  requestPasswordResetByEmail,
  getCurrentSessionState
} from "../web/src/services/authService.js";
import { auth } from "../web/src/firebaseClient.js";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import itkLogo from "../assets/itk_logo.png";
import innovationLoginImage from "../assets/innovation_login.png";

const PRIMARY_BLUE = "#1565C0";
const PAGE_BG = "#f4f7fb";
const CARD_BG = "#ffffff";
const TEXT_DARK = "#0f172a";
const TEXT_MUTED = "#64748b";
const BORDER = "#dbe3ee";

export default function LoginPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.innerWidth < 992
      : false
  );

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.overflowX = "hidden";

    return () => {
      document.body.style.overflowX = "";
    };
  }, []);

  /*
  |--------------------------------------------------------------------------
  | EXISTING SESSION CONTROL
  |--------------------------------------------------------------------------
  |
  | Kullanıcı daha önce giriş yaptıysa Firebase Auth oturumu burada
  | kontrol edilir.
  |
  | mustChangePassword = true ise kullanıcı force-reset ekranına gider.
  | false ise normal uygulamaya devam eder.
  |
  */

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (user) => {
        try {
          if (!user) {
            setCheckingSession(false);
            return;
          }

          const session =
            await getCurrentSessionState();

          if (!session) {
            setCheckingSession(false);
            return;
          }

          if (session.mustChangePassword) {
            navigate("/force-reset", {
              replace: true,
              state: {
                uid: session.uid,
                email: session.email
              }
            });

            return;
          }

          navigate("/", {
            replace: true
          });

        } catch (err) {
          console.error(
            "SESSION CHECK ERROR:",
            err
          );

          setCheckingSession(false);
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [navigate]);

  /*
  |--------------------------------------------------------------------------
  | LOGIN
  |--------------------------------------------------------------------------
  */

  async function handleLogin(e) {
    e.preventDefault();

    setError("");
    setInfo("");
    setLoading(true);

    try {
      const session =
        await loginAndResolvePasswordState(
          email.trim().toLowerCase(),
          password
        );

      /*
       * Kullanıcı ilk giriş sürecindeyse zorunlu
       * şifre değiştirme ekranına gönder.
       */
      if (session.mustChangePassword) {
        navigate("/force-reset", {
          state: {
            uid: session.uid,
            email: session.email
          }
        });

        return;
      }

      /*
       * Normal kullanıcı.
       */
      navigate("/");

    } catch (err) {
      console.error(
        "LOGIN ERROR:",
        err
      );

      if (
        err?.code === "auth/user-not-found"
      ) {
        setError(
          "Bu email adresine ait kullanıcı bulunamadı."
        );
      } else if (
        err?.code === "auth/wrong-password"
      ) {
        setError(
          "Şifre hatalı."
        );
      } else if (
        err?.code === "auth/invalid-credential"
      ) {
        setError(
          "Email veya şifre hatalı."
        );
      } else {
        setError(
          err?.code ||
          err?.message ||
          "Giriş başarısız."
        );
      }

    } finally {
      setLoading(false);
    }
  }

  /*
  |--------------------------------------------------------------------------
  | FORGOT PASSWORD
  |--------------------------------------------------------------------------
  */

  async function handleForgotPassword() {
    setError("");
    setInfo("");

    const normalizedEmail =
      email.trim().toLowerCase();

    if (!normalizedEmail) {
      setError(
        "Lütfen önce email adresinizi girin."
      );

      return;
    }

    try {
      setForgotLoading(true);

      await requestPasswordResetByEmail(
        normalizedEmail
      );

      setInfo(
        "Şifre yenileme bağlantısı email adresinize gönderildi."
      );

    } catch (err) {
      console.error(
        "FORGOT PASSWORD ERROR:",
        err
      );

      setError(
        err?.code ||
        err?.message ||
        "Şifre yenileme maili gönderilemedi."
      );

    } finally {
      setForgotLoading(false);
    }
  }

  /*
  |--------------------------------------------------------------------------
  | SESSION LOADING
  |--------------------------------------------------------------------------
  */

  if (checkingSession) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingCard}>
          Oturum kontrol ediliyor...
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        ...styles.page,
        gridTemplateColumns:
          isMobile
            ? "1fr"
            : "1fr 1fr"
      }}
    >

      {!isMobile && (
        <div style={styles.leftColumn}>
          <div style={styles.leftVisualCard}>
            <img
              src={innovationLoginImage}
              alt="Innovation Image"
              style={styles.leftImage}
            />
          </div>
        </div>
      )}

      <div
        style={{
          ...styles.rightColumn,
          padding:
            isMobile
              ? "20px 16px"
              : "32px"
        }}
      >

        <form
          style={{
            ...styles.formCard,
            padding:
              isMobile
                ? "28px 20px"
                : "36px 34px"
          }}
          onSubmit={handleLogin}
        >

          <img
            src={itkLogo}
            alt="İTK Logo"
            style={styles.logo}
          />

          <label style={styles.label}>
            Email
          </label>

          <div style={styles.inputWrapper}>
            <Mail
              size={18}
              strokeWidth={2}
              style={styles.inputIcon}
            />

            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={(e) =>
                setEmail(e.target.value)
              }
              placeholder="admin@company.com"
            />
          </div>

          <label style={styles.label}>
            Şifre
          </label>

          <div style={styles.inputWrapper}>

            <Lock
              size={18}
              strokeWidth={2}
              style={styles.inputIcon}
            />

            <input
              style={styles.passwordInput}
              type={
                showPassword
                  ? "text"
                  : "password"
              }
              value={password}
              onChange={(e) =>
                setPassword(e.target.value)
              }
              placeholder="Şifrenizi girin"
            />

            <button
              type="button"
              onClick={() =>
                setShowPassword(
                  (prev) => !prev
                )
              }
              style={styles.eyeButton}
              aria-label={
                showPassword
                  ? "Şifreyi gizle"
                  : "Şifreyi göster"
              }
              title={
                showPassword
                  ? "Şifreyi gizle"
                  : "Şifreyi göster"
              }
            >
              {showPassword ? (
                <EyeOff
                  size={18}
                  strokeWidth={2.1}
                />
              ) : (
                <Eye
                  size={18}
                  strokeWidth={2.1}
                />
              )}
            </button>

          </div>

          <div style={styles.forgotRow}>

            <button
              type="button"
              onClick={
                handleForgotPassword
              }
              disabled={
                forgotLoading
              }
              style={
                styles.linkButton
              }
            >
              {forgotLoading
                ? "Gönderiliyor..."
                : "Şifremi unuttum"}
            </button>

          </div>

          <button
            style={styles.button}
            type="submit"
            disabled={loading}
          >
            {loading
              ? "Giriş yapılıyor..."
              : "Giriş Yap"}
          </button>

          <p style={styles.contactInfo}>
            İzmir Türk Koleji öğretmeni
            değilseniz, başvuru için
            bizimle{" "}
            <a
              href="mailto:innovationhub@itk.k12.tr"
              style={
                styles.contactLink
              }
            >
              innovationhub@itk.k12.tr
            </a>{" "}
            e-posta adresi üzerinden
            iletişime geçebilirsiniz.
          </p>

          {info && (
            <p style={styles.info}>
              {info}
            </p>
          )}

          {error && (
            <p style={styles.error}>
              {error}
            </p>
          )}

        </form>

      </div>

    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    width: "100%",
    display: "grid",
    overflowX: "hidden",
    background:
      "linear-gradient(135deg, #f8fbff 0%, #f4f7fb 48%, #eef4fb 100%)",
    fontFamily:
      '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    boxSizing: "border-box"
  },

  leftColumn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px",
    background: PRIMARY_BLUE,
    minWidth: 0,
    boxSizing: "border-box"
  },

  leftVisualCard: {
    width: "100%",
    maxWidth: "720px",
    minHeight: "80vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    boxShadow: "none",
    borderRadius: 0,
    padding: "48px",
    minWidth: 0,
    boxSizing: "border-box"
  },

  leftImage: {
    width: "80%",
    maxWidth: "600px",
    height: "auto",
    objectFit: "contain",
    display: "block",
    margin: "0 auto",
    borderRadius: "18px",
    padding: "12px",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    boxShadow:
      "0 10px 24px rgba(0,0,0,0.08)",
    boxSizing: "border-box"
  },

  rightColumn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px",
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box"
  },

  formCard: {
    width: "100%",
    maxWidth: "460px",
    minWidth: 0,
    background: CARD_BG,
    padding: "36px 34px",
    borderRadius: "24px",
    boxSizing: "border-box",
    boxShadow:
      "0 30px 70px rgba(15, 23, 42, 0.18), 0 12px 28px rgba(15, 23, 42, 0.10)",
    border:
      "1px solid rgba(15, 23, 42, 0.08)"
  },

  logo: {
    width: "78px",
    height: "78px",
    objectFit: "contain",
    display: "block",
    margin: "0 auto 22px auto"
  },

  title: {
    margin: 0,
    textAlign: "center",
    fontSize: "30px",
    fontWeight: 700,
    color: TEXT_DARK,
    letterSpacing: "-0.02em"
  },

  subtitle: {
    marginTop: "10px",
    marginBottom: "26px",
    textAlign: "center",
    fontSize: "14px",
    lineHeight: 1.65,
    color: TEXT_MUTED
  },

  label: {
    display: "block",
    marginTop: 14,
    marginBottom: 8,
    fontWeight: 600,
    color: "#1e293b",
    fontSize: "14px"
  },

  inputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    minWidth: 0,
    minHeight: "52px",
    padding: "0 14px",
    borderRadius: "14px",
    border: `1px solid ${BORDER}`,
    background: "#ffffff",
    boxSizing: "border-box"
  },

  inputIcon: {
    color: "#64748b",
    flexShrink: 0
  },

  input: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    height: "50px",
    border: "none",
    outline: "none",
    fontSize: "14px",
    color: TEXT_DARK,
    background: "transparent"
  },

  passwordInput: {
    flex: 1,
    minWidth: 0,
    width: "100%",
    height: "50px",
    border: "none",
    outline: "none",
    fontSize: "14px",
    color: TEXT_DARK,
    background: "transparent"
  },

  eyeButton: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    flexShrink: 0
  },

  forgotRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 10
  },

  linkButton: {
    border: "none",
    background: "transparent",
    color: PRIMARY_BLUE,
    cursor: "pointer",
    padding: 0,
    fontSize: 14,
    fontWeight: 500
  },

  button: {
    width: "100%",
    marginTop: 22,
    minHeight: "52px",
    padding: "13px 16px",
    border: "none",
    borderRadius: "14px",
    cursor: "pointer",
    background: PRIMARY_BLUE,
    color: "#fff",
    fontSize: "15px",
    fontWeight: 600,
    boxShadow:
      "0 10px 24px rgba(21, 101, 192, 0.24)"
  },

  error: {
    color: "crimson",
    marginTop: 14,
    fontSize: "14px",
    lineHeight: 1.5
  },

  info: {
    color: "green",
    marginTop: 14,
    fontSize: "14px",
    lineHeight: 1.5
  },

  loadingContainer: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: PAGE_BG,
    overflowX: "hidden",
    boxSizing: "border-box"
  },

  loadingCard: {
    width: 420,
    maxWidth: "calc(100% - 32px)",
    background: "#fff",
    padding: 24,
    borderRadius: 16,
    boxShadow:
      "0 12px 28px rgba(0,0,0,0.08)",
    textAlign: "center",
    fontFamily:
      '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    boxSizing: "border-box"
  },

  contactInfo: {
    marginTop: 18,
    fontSize: "13px",
    lineHeight: 1.6,
    textAlign: "center",
    color: "#64748b"
  },

  contactLink: {
    color: PRIMARY_BLUE,
    fontWeight: 600,
    textDecoration: "none"
  }
};